import { ReactElement, useMemo } from 'react';
import { useToggler } from '@cbhq/cds-common';
import { UiIconName } from '@cbhq/cds-icons';
import { Banner } from '@cbhq/cds-web/banner/Banner';
import { Button, ButtonGroup } from '@cbhq/cds-web/buttons';
import { useBreakpoints } from '@cbhq/cds-web/hooks/useBreakpoints';
import { Icon } from '@cbhq/cds-web/icons';
import { Box, Grid, HStack, VStack } from '@cbhq/cds-web/layout';
import { Pressable } from '@cbhq/cds-web/system';
import { TextBody, TextDisplay2, TextTitle1, TextTitle4 } from '@cbhq/cds-web/typography';

import { Layout } from '../layout';

import { FULL_PAGE_MAX_WIDTH, MAIN_CONTAINER_MIN_WIDTH } from './constants';
import { KeyFeatureItem, KeyFeaturesSection } from './KeyFeaturesSection';
import { ProductLandingTemplateSpecs } from './ProductLandingTemplateSpecs';
import { SpecProps } from './types';

export type BannerItem = {
  title: string;
  description: string;
  cta?: string;
  onPress?: () => void;
  image: ReactElement;
  buttonEndIcon?: UiIconName;
};

type BannerSectionProps = {
  bannerItem1: BannerItem;
  bannerItem2: BannerItem;
};

function BannerSectionItem({ title, description, cta, onPress, image, buttonEndIcon }: BannerItem) {
  return (
    <VStack maxWidth={540} justifyContent="space-between">
      <VStack spacingStart={2}>
        <TextTitle1 as="h1">{title}</TextTitle1>
        <TextBody as="p" color="foregroundMuted" spacingTop={3} spacingBottom={7}>
          {description}
        </TextBody>
      </VStack>
      <VStack alignItems="flex-start" gap={2}>
        <Box spacingStart={2}>{image}</Box>
        {cta && onPress && (
          <Button onPress={onPress} transparent endIcon={buttonEndIcon} compact>
            {cta}
          </Button>
        )}
      </VStack>
    </VStack>
  );
}

function BannerSection({ bannerItem1, bannerItem2 }: BannerSectionProps) {
  const { isDesktop } = useBreakpoints();
  const responsiveTemplateColumns = useMemo(() => {
    return isDesktop ? '1fr 1fr' : '1fr';
  }, [isDesktop]);

  return (
    <VStack borderedBottom>
      <Grid
        spacingStart={7}
        spacingEnd={9}
        spacingVertical={9}
        templateColumns={responsiveTemplateColumns}
        gap={9}
        maxWidth={FULL_PAGE_MAX_WIDTH}
      >
        <BannerSectionItem {...bannerItem1} />
        <BannerSectionItem {...bannerItem2} />
      </Grid>
    </VStack>
  );
}

type ExpandableContentSectionItem = {
  title: string;
  content: ReactElement;
};

function ExpandableContentSection({ title, content }: ExpandableContentSectionItem) {
  const [isDrawerOpen, { toggleOn: toggleDrawerOpen, toggleOff: toggleDrawerClose }] =
    useToggler(false);

  return (
    <VStack spacing={9} borderedTop>
      <Pressable
        background="transparent"
        onPress={isDrawerOpen ? toggleDrawerClose : toggleDrawerOpen}
        noScaleOnPress
      >
        <HStack
          justifyContent="space-between"
          alignItems="center"
          spacingBottom={isDrawerOpen ? 9 : 0}
        >
          <TextTitle1 as="h1">{title}</TextTitle1>
          <Icon color="foreground" name={isDrawerOpen ? 'caretUp' : 'caretDown'} size="l" />
        </HStack>
      </Pressable>
      {isDrawerOpen && content}
    </VStack>
  );
}

type ProductLandingPageTemplateProps = {
  maxWidth: number;
  title: string;
  description: string;
  primaryCTA?: string;
  primaryOnPress?: () => void;
  secondaryCTA?: string;
  secondaryOnPress?: () => void;
  specs: SpecProps[];
  bannerItem1: BannerItem;
  bannerItem2: BannerItem;
  keyFeatures: KeyFeatureItem[];
  showEarlyAccessBanner?: boolean;
  expandableContentSections?: ExpandableContentSectionItem[];
};

const bannerStyle = { width: '100%' };

export function ProductLandingPageTemplate({
  maxWidth,
  title,
  description,
  primaryCTA,
  primaryOnPress,
  secondaryCTA,
  secondaryOnPress,
  specs,
  bannerItem1,
  bannerItem2,
  keyFeatures,
  expandableContentSections,
  showEarlyAccessBanner = false,
}: ProductLandingPageTemplateProps) {
  const { isDesktop } = useBreakpoints();
  const responsiveTemplateColumns = useMemo(() => {
    return isDesktop ? '1fr 1fr' : '1fr';
  }, [isDesktop]);

  return (
    <Layout enableMaxWidth={false}>
      <Layout.MainContainer minWidth={MAIN_CONTAINER_MIN_WIDTH} spacingHorizontal={0} fullWidth>
        <Box display="block" width="100%">
          <VStack>
            {showEarlyAccessBanner && (
              <VStack spacing={9} spacingBottom={0}>
                <Banner
                  style={bannerStyle}
                  title="Early access product"
                  variant="promotional"
                  startIcon="info"
                >
                  This product is currently in preview. To gain exclusive access, get in touch with
                  our dedicated sales team.
                </Banner>
              </VStack>
            )}
            <HStack borderedBottom>
              <Grid
                maxWidth={maxWidth}
                gap={9}
                spacing={9}
                templateColumns={responsiveTemplateColumns}
              >
                <VStack>
                  <TextDisplay2 as="h1">{title}</TextDisplay2>
                  <TextTitle4 as="h2" color="foregroundMuted" spacingTop={5} spacingBottom={7}>
                    {description}
                  </TextTitle4>

                  <ButtonGroup>
                    {!!primaryCTA && primaryOnPress && (
                      <Button variant="primary" endIcon="forwardArrow" onPress={primaryOnPress}>
                        {primaryCTA}
                      </Button>
                    )}
                    {!!secondaryCTA && secondaryOnPress && (
                      <Button variant="secondary" endIcon="externalLink" onPress={secondaryOnPress}>
                        {secondaryCTA}
                      </Button>
                    )}
                  </ButtonGroup>
                </VStack>
                <ProductLandingTemplateSpecs specs={specs} />
              </Grid>
            </HStack>
            <BannerSection bannerItem1={bannerItem1} bannerItem2={bannerItem2} />
            <KeyFeaturesSection features={keyFeatures} />
            {expandableContentSections?.map((section) => (
              <ExpandableContentSection key={section.title} {...section} />
            ))}
          </VStack>
        </Box>
      </Layout.MainContainer>
    </Layout>
  );
}
